import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { HFP_V2_SCHEMAS, HFP_V2_SCHEMAS_LIST } from '../schemas/hfpV2';
import HfpV2Report from '../components/hfp/HfpV2Report';

// ────────────────────────────────────────────────────────────────────────────
// CONSTANTS
// ────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'hfp-v2-demo-answers';
const RESPONDENT_KEY = 'hfp-v2-demo-respondent';
const SCORING_KEY = 'hfp-v2-demo-scoring';
const REPORT_KEY = 'hfp-v2-demo-report';

/**
 * Respondent labels displayed in the toggle. "home" maps to parent/guardian
 * answers; "school" maps to teacher answers. Both are stored independently
 * so the scoring engine can compare capacity vs. performance across contexts.
 */
const RESPONDENT_LABELS = {
  home: 'Parent / Guardian (Home)',
  school: 'Teacher (School)',
};

/**
 * Simulation profiles control the distribution of randomly-generated answers
 * when the user clicks "Simulate All Answers". Each profile produces a
 * recognisable pattern that exercises different scoring-engine paths.
 *
 * Weights correspond to answer option indices (0 = most positive, last = most
 * negative). The engine normalises them so they don't need to sum to 1.
 */
const SIMULATION_PROFILES = {
  typical: { label: 'Typical', weights6: [5, 15, 60, 10, 7, 3], weights5: [5, 15, 60, 15, 5] },
  efDominant: { label: 'EF-Dominant Difficulty', weights6: [2, 5, 20, 30, 30, 13], weights5: [2, 10, 20, 40, 28] },
  sensoryCascade: { label: 'Sensory Cascade', weights6: [3, 8, 30, 25, 22, 12], weights5: [5, 20, 30, 30, 15] },
  pervasive: { label: 'Pervasive Difficulty', weights6: [1, 3, 10, 25, 35, 26], weights5: [2, 5, 15, 40, 38] },
  strength: { label: 'Broad Strengths', weights6: [30, 35, 25, 7, 2, 1], weights5: [35, 30, 25, 7, 3] },
};

/**
 * Context agreement presets control how much the School profile diverges
 * from the Home profile during simulation. Instead of generating both
 * contexts independently (which tends towards high agreement), we first
 * pick a Home answer then derive the School answer by applying a random
 * shift whose magnitude is controlled by these presets.
 *
 * - shiftProb: probability (0–1) that any given School answer is shifted
 *   away from the Home answer. 0 = identical, 1 = always shifted.
 * - maxShift: maximum number of option positions the School answer can
 *   be shifted (positive = toward more difficulty, direction is random).
 * - directionBias: probability (0–1) that the shift pushes toward MORE
 *   difficulty at school. 0.5 = equally likely in either direction;
 *   0.7 = 70% chance school is worse than home.
 */
/**
 * Context agreement presets now use a two-layer approach to produce
 * realistic domain-level divergence, not just item-level noise:
 *
 * Layer 1 — Systematic per-form shift:
 *   For each form (≈ domain), a consistent direction and magnitude are
 *   chosen. ALL items in that form are shifted in the same direction.
 *   This prevents the "averaging out" effect that occurs when per-item
 *   random shifts cancel each other at the domain-mean level.
 *
 * - domainShiftMin / domainShiftMax: range for the systematic shift
 *   magnitude (in option positions) applied to every item in a form.
 * - domainShiftProb: probability (0–1) that any given form receives a
 *   systematic shift at all (unshifted forms stay close to Home).
 * - directionBias: probability (0–1) the systematic direction pushes
 *   toward more difficulty at school (> 0.5 = school worse).
 *
 * Layer 2 — Per-item noise (unchanged from original design):
 * - itemNoise: max additional random jitter per item (on top of the
 *   systematic shift). This prevents every item in a form from being
 *   shifted by exactly the same amount, which would look artificial.
 * - itemNoiseProb: probability an individual item receives extra noise.
 */
const CONTEXT_AGREEMENT_PRESETS = {
  high: {
    label: 'High Agreement',
    domainShiftProb: 0.15,
    domainShiftMin: 0,
    domainShiftMax: 1,
    directionBias: 0.5,
    itemNoise: 1,
    itemNoiseProb: 0.1,
  },
  moderate: {
    label: 'Moderate Divergence',
    domainShiftProb: 0.6,
    domainShiftMin: 1,
    domainShiftMax: 2,
    directionBias: 0.6,
    itemNoise: 1,
    itemNoiseProb: 0.3,
  },
  low: {
    label: 'Low Agreement',
    domainShiftProb: 0.85,
    domainShiftMin: 2,
    domainShiftMax: 4,
    directionBias: 0.65,
    itemNoise: 1,
    itemNoiseProb: 0.4,
  },
};

// ────────────────────────────────────────────────────────────────────────────
// LLM MODEL & MODE OPTIONS
// ────────────────────────────────────────────────────────────────────────────
// The live model list is fetched from the rmsHfpV2DemoModels callable on page
// load (single source of truth: AVAILABLE_MODELS in
// python-functions/rms_functions/hfp_v2/llm_router.py). This list is only the
// fallback while loading or if the callable is unreachable.
const FALLBACK_LLM_MODELS = [
  { id: 'gemini-3.1-flash-lite', label: 'Gemini 3.1 Flash Lite (Vertex AI, EU)', provider: 'vertex' },
];
const FALLBACK_MODEL_ID = 'gemini-3.1-flash-lite';

// Design decision: reports are always LLM-enhanced. The rules-based
// pipeline still runs as the deterministic foundation, but LLM polish
// is always applied on top. The mode toggle has been removed from the UI.
const REPORT_MODE = 'llm';

// Pronoun sets supported by the report engine (mirrors the production
// referral-driven resolution in hfp_v2_integration.py; defaults to the
// gender-neutral singular "they/them").
const PRONOUN_OPTIONS = [
  { id: 'they/them', label: 'They / them' },
  { id: 'he/him', label: 'He / him' },
  { id: 'she/her', label: 'She / her' },
  { id: 'ze/zir', label: 'Ze / zir' },
];

// ────────────────────────────────────────────────────────────────────────────
// HELPERS
// ────────────────────────────────────────────────────────────────────────────

/**
 * Load saved answers from localStorage. Returns an object keyed by
 * respondent ("home" / "school"), then formKey, then questionId → optionId.
 */
function loadAnswers() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : { home: {}, school: {} };
  } catch {
    return { home: {}, school: {} };
  }
}

function saveAnswers(answers) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(answers));
  } catch { /* quota exceeded – non-critical for demo */ }
}

function loadRespondent() {
  try {
    return localStorage.getItem(RESPONDENT_KEY) || 'home';
  } catch {
    return 'home';
  }
}

function loadSessionData(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveSessionData(key, data) {
  try {
    if (data == null) {
      localStorage.removeItem(key);
    } else {
      localStorage.setItem(key, JSON.stringify(data));
    }
  } catch { /* quota exceeded – non-critical for demo */ }
}

function clearAllSessionData() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(RESPONDENT_KEY);
    localStorage.removeItem(SCORING_KEY);
    localStorage.removeItem(REPORT_KEY);
  } catch { /* noop */ }
}

/**
 * Determines whether a question should be visible for a given respondent.
 * Questions with respondent="both" are always visible; "lg_only" maps to
 * "home" and "school_only" maps to "school".
 */
function isVisibleForRespondent(question, respondent) {
  const r = question?.meta?.hfp?.respondent || 'both';
  if (r === 'both') return true;
  if (r === 'lg_only' && respondent === 'home') return true;
  if (r === 'school_only' && respondent === 'school') return true;
  return false;
}

/**
 * Weighted random selection: pick an index from 0..weights.length-1
 * using the provided unnormalised weight array.
 */
function weightedRandom(weights) {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r <= 0) return i;
  }
  return weights.length - 1;
}

// ────────────────────────────────────────────────────────────────────────────
// BADGE COMPONENT
// ────────────────────────────────────────────────────────────────────────────

function StatusBadge({ progress }) {
  if (progress >= 100) {
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">Complete</span>;
  }
  if (progress > 0) {
    return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">In Progress</span>;
  }
  return <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">Not Started</span>;
}

// ────────────────────────────────────────────────────────────────────────────
// INLINE FORM RENDERER
// ────────────────────────────────────────────────────────────────────────────
// A self-contained form viewer that avoids the Firebase / analytics / auth
// dependencies baked into RmsFormTemplate. Renders one section at a time
// with simple radio groups, backed entirely by React state + localStorage.

function DemoFormView({ schema, answers, setAnswer, respondent, onClose }) {
  const [activeSectionIdx, setActiveSectionIdx] = useState(0);

  // Filter sections: only show sections that have at least one visible question
  const visibleSections = useMemo(() => {
    return (schema?.sections || []).filter((sec) =>
      (sec.questions || []).some((q) => isVisibleForRespondent(q, respondent))
    );
  }, [schema, respondent]);

  const currentSection = visibleSections[activeSectionIdx];
  const visibleQuestions = useMemo(() => {
    return (currentSection?.questions || []).filter((q) => {
      if (!isVisibleForRespondent(q, respondent)) return false;
      // Hide conditional checkbox questions when their gate isn't met —
      // these should only appear when the parent question has the
      // required answer (e.g. b1301 = "Much more" to show seeking modifiers).
      const cond = q.meta?.hfp?.conditionalOn;
      if (cond) {
        const gateAnswer = answers?.[cond.questionId];
        if (gateAnswer !== cond.value) return false;
      }
      return true;
    });
  }, [currentSection, respondent, answers]);

  // Derive the framing question from the first visible non-checkbox question's
  // metadata (checkbox questions have a different framing).
  const framingQuestion = (
    visibleQuestions.find((q) => q.meta?.hfp?.stemType !== 'Checkbox')?.meta?.hfp?.framingQuestion || ''
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-primary/5 to-primary/10 px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{schema.title}</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              Section {activeSectionIdx + 1} of {visibleSections.length}: {currentSection?.title}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white/60 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>

      {/* Framing question banner */}
      {framingQuestion && (
        <div className="px-6 py-3 bg-blue-50 border-b border-blue-100">
          <p className="text-sm font-medium text-blue-800 italic">&ldquo;{framingQuestion}&rdquo;</p>
        </div>
      )}

      {/* Questions */}
      <div className="px-6 py-4 space-y-6 max-h-[60vh] overflow-y-auto">
        {visibleQuestions.map((q, qIdx) => {
          const currentAnswer = answers?.[q.questionId];
          const isCheckbox = q.meta?.hfp?.stemType === 'Checkbox';

          // Render a sub-heading before the first conditional checkbox in a
          // group so the user understands these are follow-up tick-boxes.
          const showCheckboxHeading = isCheckbox && (
            qIdx === 0 || visibleQuestions[qIdx - 1]?.meta?.hfp?.stemType !== 'Checkbox'
          );
          const checkboxFraming = q.meta?.hfp?.framingQuestion || '';

          return (
            <div key={q.questionId}>
              {showCheckboxHeading && checkboxFraming && (
                <div className="mb-3 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <p className="text-xs font-medium text-indigo-700 italic">{checkboxFraming}</p>
                </div>
              )}
              <div className={`pb-4 last:border-0 ${isCheckbox ? 'pl-4 border-l-2 border-indigo-200' : 'border-b border-gray-100'}`}>
                <p className="text-sm font-medium text-gray-800 mb-2">
                  <span className="text-xs text-gray-400 mr-1.5">{q.meta?.hfp?.itemCode}</span>
                  {q.label}
                </p>
                <div className="flex flex-wrap gap-2">
                  {isCheckbox ? (
                    // Checkbox: render as a single toggle (ticked / not ticked)
                    <button
                      type="button"
                      onClick={() => setAnswer(
                        schema.formKey, q.questionId,
                        currentAnswer === 'ao_1' ? 'ao_2' : 'ao_1'
                      )}
                      className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                        currentAnswer === 'ao_1'
                          ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-indigo-400 hover:bg-indigo-50'
                      }`}
                    >
                      {currentAnswer === 'ao_1' ? 'Yes' : 'No'}
                    </button>
                  ) : (
                    // Standard radio question
                    (q.options || []).map((opt) => {
                      const selected = currentAnswer === opt.value;
                      return (
                        <button
                          key={opt.optionId}
                          type="button"
                          onClick={() => setAnswer(schema.formKey, q.questionId, opt.value)}
                          className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                            selected
                              ? 'bg-primary text-white border-primary shadow-sm'
                              : 'bg-white text-gray-700 border-gray-300 hover:border-primary/50 hover:bg-primary/5'
                          }`}
                        >
                          {opt.label} <span className="opacity-60">({opt.meta?.score})</span>
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Section navigation */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
        <button
          type="button"
          disabled={activeSectionIdx === 0}
          onClick={() => setActiveSectionIdx((i) => Math.max(0, i - 1))}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          Previous
        </button>
        <span className="text-xs text-gray-500">
          {activeSectionIdx + 1} / {visibleSections.length}
        </span>
        {activeSectionIdx < visibleSections.length - 1 ? (
          <button
            type="button"
            onClick={() => setActiveSectionIdx((i) => Math.min(visibleSections.length - 1, i + 1))}
            className="px-4 py-2 text-sm font-medium text-white bg-primary border border-primary rounded-lg hover:bg-primary/90 transition-colors"
          >
            Next
          </button>
        ) : (
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-green-600 rounded-lg hover:bg-green-700 transition-colors"
          >
            Done
          </button>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// JSON VIEWER
// ────────────────────────────────────────────────────────────────────────────
// Renders a collapsible panel with syntax-highlighted JSON. Used for the
// raw scoring output and any debug payloads.

function JsonPanel({ title, data, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const json = useMemo(() => {
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return '{}';
    }
  }, [data]);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
      >
        <span className="text-sm font-semibold text-gray-700">{title}</span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <pre className="px-5 py-4 text-xs text-gray-800 bg-white overflow-x-auto max-h-[60vh] font-mono">
          {json}
        </pre>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// SCORING DISPLAY — human-readable structured scoring output
// ────────────────────────────────────────────────────────────────────────────
// Replaces the raw JSON panel with a tabbed, explanatory view that walks
// reviewers through the 3-phase scoring pipeline in plain English.
// Each section explains WHAT the engine computed, WHY it matters,
// and WHAT the result means.

/**
 * Maps classification labels to Tailwind badge classes.
 * Shared across the scoring and report displays.
 */
const CLS_BADGE = {
  'Strength': 'bg-green-100 text-green-800 border-green-200',
  'No difficulty': 'bg-green-50 text-green-700 border-green-200',
  'Mild difficulty': 'bg-yellow-100 text-yellow-800 border-yellow-200',
  'Moderate or severe difficulty': 'bg-red-100 text-red-800 border-red-200',
  'Sensory (see profile tags)': 'bg-indigo-50 text-indigo-700 border-indigo-200',
  'Environmental (see SAI)': 'bg-teal-50 text-teal-700 border-teal-200',
};

/**
 * Plain-English explanations for each classification label. Shown in
 * tooltips next to ClsBadge so a reviewer can understand the thresholds
 * without needing the UCR document open.
 */
const CLS_TOOLTIP = {
  'Strength': 'Mean is above 0 — the child is performing above peer expectations in this area.',
  'No difficulty': 'Mean is exactly 0 — functioning is in line with peers.',
  'Mild difficulty': 'Mean is around -1 — a small difference from peers that may not require intervention.',
  'Moderate or severe difficulty': 'Mean is -2 or below — a significant gap from peers that likely impacts daily functioning.',
  'Sensory (see profile tags)': 'Sensory domains aren\'t classified on the standard scale. See Sensory Profile Tags in Phase 2 for directional patterns (e.g. hyper/hypo-sensitive).',
  'Environmental (see SAI)': 'Environmental items measure how well the child\'s surroundings meet their needs, not the child\'s abilities. See the Support Adequacy Index (SAI) in Phase 2.',
};

/**
 * Builds a tooltip string that lists every possible value for a
 * classification, highlighting which one is currently active. Used across
 * the scoring panel so reviewers can see at a glance what the engine
 * "could have" chosen and why it chose what it did.
 *
 * @param {Array<{value: string, desc: string}>} options - ordered list
 * @param {string|null} current - the currently selected value
 * @returns {string} multiline tooltip text
 */
function possibleValuesTooltip(options, current) {
  return options.map(({ value, desc }) => {
    const marker = value === current ? ' ◀ ACTIVE' : '';
    return `${value}${marker}\n  ${desc}`;
  }).join('\n\n');
}

const AGREEMENT_OPTIONS = [
  { value: 'High', desc: '|diff| ≤ 1.0 — both respondents see the child similarly.' },
  { value: 'Moderate', desc: '1.0 < |diff| ≤ 2.0 — noticeable difference between respondents.' },
  { value: 'Low', desc: '|diff| > 2.0 — substantially different pictures; warrants investigation.' },
];

const SAI_OPTIONS = [
  { value: 'SUFFICIENT', desc: 'Environmental support meets or exceeds 2× the number of impaired domains.' },
  { value: 'INSUFFICIENT', desc: 'Not enough environmental support for the level of difficulty.' },
  { value: 'NOT APPLICABLE', desc: 'No domains at moderate/severe difficulty, so SAI is not calculated.' },
];

const DIVERGENCE_OPTIONS = [
  { value: 'Pervasive Item-Level Difficulty', desc: 'Both Home ≤ -2 and School ≤ -2 — difficulty in both settings.' },
  { value: 'Home-Dominant Difficulty', desc: 'Home ≤ -2, School > -1 — difficulty mainly at home.' },
  { value: 'School-Dominant Difficulty', desc: 'School ≤ -2, Home > -1 — difficulty mainly at school.' },
  { value: null, desc: 'No significant divergence pattern.' },
];

const DOMAIN_CLS_OPTIONS = [
  { value: 'Strength', desc: 'Mean > 0 — above peer expectations.' },
  { value: 'No difficulty', desc: 'Mean = 0 — in line with peers.' },
  { value: 'Mild difficulty', desc: 'Mean ≈ -1 — small gap, may not need intervention.' },
  { value: 'Moderate or severe difficulty', desc: 'Mean ≤ -2 — significant gap, likely impacts daily life.' },
];

/**
 * Hover-triggered tooltip that wraps any children. Uses a React portal
 * to render the floating text box at the document body level with fixed
 * positioning. This ensures the tooltip is never clipped by ancestor
 * containers with overflow-hidden (e.g. tables, rounded-corner wrappers).
 */
function Tooltip({ text, children, maxWidth = 280 }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);

  if (!text) return children;

  const handleEnter = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.top - 6,
        left: rect.left + rect.width / 2,
      });
    }
    setVisible(true);
  };

  return (
    <span
      ref={triggerRef}
      className="inline-flex items-center"
      onMouseEnter={handleEnter}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && createPortal(
        <span
          className="pointer-events-none"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform: 'translate(-50%, -100%)',
            zIndex: 99999,
            width: `${maxWidth}px`,
          }}
        >
          <span className="block rounded-lg bg-gray-900 text-white text-xs leading-relaxed px-3 py-2 shadow-lg text-left whitespace-normal">
            {text}
          </span>
        </span>,
        document.body
      )}
    </span>
  );
}

/**
 * Convenience component: a small "?" circle that shows a tooltip on hover.
 * Drop this next to any label or value that needs a plain-English explanation.
 */
function InfoTip({ text, maxWidth }) {
  return (
    <Tooltip text={text} maxWidth={maxWidth}>
      <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-gray-600 text-[10px] font-bold cursor-help ml-1 hover:bg-gray-300 transition-colors flex-shrink-0">
        ?
      </span>
    </Tooltip>
  );
}

/** Pill badge for classification labels, with a tooltip listing all possible tiers */
function ClsBadge({ label }) {
  const cls = CLS_BADGE[label] || 'bg-gray-100 text-gray-600 border-gray-200';
  const isSpecial = label === 'Sensory (see profile tags)' || label === 'Environmental (see SAI)';
  const tip = isSpecial
    ? CLS_TOOLTIP[label]
    : possibleValuesTooltip(DOMAIN_CLS_OPTIONS, label);
  const badge = (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border cursor-help ${cls}`}>
      {label || '—'}
    </span>
  );
  return <Tooltip text={tip} maxWidth={340}>{badge}</Tooltip>;
}

/** Contextual explanation block — light blue info box with darker readable text */
function ExplainBox({ children }) {
  return (
    <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-gray-700 leading-relaxed">
      {children}
    </div>
  );
}

/** Pill for boolean flags (active/inactive) */
function FlagPill({ label, active, detail }) {
  return (
    <div className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
      active
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : 'bg-gray-50 text-gray-500 border-gray-200'
    }`}>
      <span className={`w-2 h-2 rounded-full ${active ? 'bg-amber-500' : 'bg-gray-300'}`} />
      {label}
      {detail && <span className="text-[10px] font-normal ml-1 opacity-75">({detail})</span>}
    </div>
  );
}

/**
 * Generates a human-readable tooltip from a sensory tag string in the
 * new format (e.g. "HYPER_VISUAL", "SEEK_AUDITORY", "LOW_REG_TACTILE").
 */
function sensoryTagTooltip(tag) {
  const TAG_TOOLTIPS = {
    HYPER: 'shows heightened sensitivity to',
    HYPO: 'shows reduced sensitivity to',
    SEEK: 'actively seeks input from',
    AVOID: 'avoids or is distressed by',
    LOW_REG: 'often misses or under-registers',
    HIGH_REG: 'shows enhanced noticing of',
  };

  for (const [prefix, desc] of Object.entries(TAG_TOOLTIPS)) {
    if (tag.startsWith(prefix + '_')) {
      const sense = tag.slice(prefix.length + 1).replace(/_/g, ' ').toLowerCase();
      return `The child ${desc} ${sense} input.`;
    }
  }
  return tag;
}

/**
 * Short label prefix lookups for the new tag format.
 */
const SENSORY_TAG_PREFIX_LABELS = {
  HYPER: 'High Sensitivity',
  HYPO: 'Low Sensitivity',
  SEEK: 'Seeking',
  AVOID: 'Avoidance',
  LOW_REG: 'Low Registration',
  HIGH_REG: 'High Registration',
};

/**
 * Converts a new-format sensory tag (e.g. "HYPER_VISUAL") into a short
 * human-readable label (e.g. "Visual: High Sensitivity") suitable
 * for compact pill display. Falls back to the raw tag if parsing fails.
 */
function sensoryTagToLabel(tag) {
  for (const [prefix, label] of Object.entries(SENSORY_TAG_PREFIX_LABELS)) {
    if (tag.startsWith(prefix + '_')) {
      const sense = tag.slice(prefix.length + 1).replace(/_/g, ' ');
      const senseTitle = sense.charAt(0) + sense.slice(1).toLowerCase();
      return `${senseTitle}: ${label}`;
    }
  }

  return tag;
}

/**
 * ScoringDisplay — The main structured scoring output viewer.
 *
 * Organises the raw Phase 1-3 scoring payload into labelled, collapsible
 * sections with plain-English explanations, coloured badges, and comparison
 * tables. Designed so a non-technical reviewer can follow how raw form
 * answers flow through to final classifications without needing to read
 * the Universal Core Rules document.
 */
function ScoringDisplay({ data }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [raStrategy, setRaStrategy] = useState('option_a');

  const phase1 = data?.phase1 || {};
  const phase2 = data?.phase2 || {};
  const reasonableAdjustments = data?.reasonable_adjustments || {};

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'phase1', label: 'Phase 1 — Scoring' },
    { id: 'phase2', label: 'Phase 2 — Comparison' },
    { id: 'adjustments', label: 'Adjustments' },
    { id: 'raw', label: 'Raw JSON' },
  ];

  // ── Overview tab ──
  const renderOverview = () => {
    const homeClsDom = phase1.home?.classifications?.domains || {};
    const schoolClsDom = phase1.school?.classifications?.domains || {};
    const modHome = phase2.modulation_summary?.home || {};
    const modSchool = phase2.modulation_summary?.school || {};

    return (
      <div className="space-y-8">
        <ExplainBox>
          <strong>How to read this:</strong> The scoring engine takes raw form answers
          from a Home respondent (parent/guardian) and a School respondent (teacher),
          then runs a 2-phase pipeline. Phase 1 scores and classifies each domain independently.
          Phase 2 compares Home vs School to find gaps, sensitivity patterns, and
          consolidated modulation flags.
        </ExplainBox>

        {/* Domain-level snapshot */}
        <section>
          <h4 className="text-lg font-bold text-gray-900 mb-1">Domain Classifications</h4>
          <p className="text-sm text-gray-600 mb-4">
            Each domain gets a classification based on the average of its subdomain scores.
            Scores above 0 = Strength, 0 = No difficulty, -1 = Mild, ≤ -2 = Moderate/Severe.
          </p>
          <div className="overflow-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <th className="px-4 py-2.5 text-left font-semibold">Domain</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Home</th>
                  <th className="px-4 py-2.5 text-left font-semibold">School</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.keys({ ...homeClsDom, ...schoolClsDom }).sort().map((domain) => (
                  <tr key={domain} className="hover:bg-gray-50/50">
                    <td className="px-4 py-2.5 font-medium text-gray-900">{domain}</td>
                    <td className="px-4 py-2.5"><ClsBadge label={homeClsDom[domain]} /></td>
                    <td className="px-4 py-2.5"><ClsBadge label={schoolClsDom[domain]} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <hr className="border-gray-200" />

        {/* Sensitivity flags */}
        <section>
          <h4 className="text-lg font-bold text-gray-900 mb-1">Modulation Flags</h4>
          <p className="text-sm text-gray-600 mb-4">
            These flags indicate whether certain cross-cutting factors (e.g. tiredness, unpredictable
            situations, complex tasks) amplify the child's difficulties beyond what domain scores alone show.
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">Home</div>
              <div className="flex flex-wrap gap-1.5">
                <FlagPill label="Fatigue" active={modHome.fatigue} />
                <FlagPill label="Complexity" active={modHome.complexity} />
                <FlagPill label="Predictability" active={modHome.predictability} />
              </div>
            </div>
            <div>
              <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">School</div>
              <div className="flex flex-wrap gap-1.5">
                <FlagPill label="Fatigue" active={modSchool.fatigue} />
                <FlagPill label="Complexity" active={modSchool.complexity} />
                <FlagPill label="Predictability" active={modSchool.predictability} />
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  };

  // ── Phase 1 tab ──
  // Shows item → subdomain → domain aggregation chain for each context.
  const renderPhase1 = () => {
    const contexts = [
      { key: 'home', label: 'Home (Parent / Guardian)', data: phase1.home },
      { key: 'school', label: 'School (Teacher)', data: phase1.school },
    ];

    return (
      <div className="space-y-8">
        <ExplainBox>
          <strong>Phase 1 — Independent Scoring</strong>: Each respondent's answers are scored
          independently. Individual item scores are averaged into <em>subdomain means</em>, which
          are then averaged into <em>domain means</em>. Each mean gets a classification label.
          <br /><br />
          <strong>Scale</strong>: Scores range from roughly +1 (strength) to -3 (severe difficulty).
          The classification thresholds are: above 0 → Strength, exactly 0 → No difficulty,
          -1 → Mild difficulty, below -1 → Moderate or severe difficulty.
        </ExplainBox>

        {contexts.map(({ key, label, data: ctxData }) => {
          if (!ctxData) return null;
          const domains = ctxData.domains || {};
          const subdomains = ctxData.subdomains || {};
          const clsDomains = ctxData.classifications?.domains || {};
          const clsSubdomains = ctxData.classifications?.subdomains || {};
          const validity = ctxData.validity || {};

          return (
            <section key={key} className="rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-5 py-3.5 bg-gray-50 border-b border-gray-200">
                <h4 className="text-base font-bold text-gray-900">{label}</h4>
              </div>
              <div className="divide-y divide-gray-200">
                {Object.keys(domains).sort().map((domain) => {
                  const domMean = domains[domain]?.mean;
                  const domCls = clsDomains[domain];
                  const subs = subdomains[domain] || {};
                  const subCls = clsSubdomains[domain] || {};
                  const domValidity = validity[domain] || {};

                  return (
                    <div key={domain} className="px-5 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2.5">
                          <span className="text-sm font-bold text-[#4a2c5a]">{domain}</span>
                          <ClsBadge label={domCls} />
                        </div>
                        <span className="text-xs font-medium text-gray-500 tabular-nums">
                          mean: {domMean != null ? domMean.toFixed(2) : '—'}
                          <InfoTip text="Average of all valid subdomain means within this domain. Invalid subdomains are excluded, not zero-filled." />
                        </span>
                      </div>
                      <div className="ml-4 space-y-1.5">
                        {Object.keys(subs).sort().map((sub) => {
                          const subMean = subs[sub]?.mean;
                          const subLabel = subCls[sub];
                          const subValid = domValidity[sub];
                          const isInvalid = subValid && !subValid.valid;
                          const action = subValid?.action;
                          const hasProrateWarning = subValid?.valid && (action === 'prorate_flag' || action === 'caution');

                          const validItemCount = subValid?.valid_item_count ?? '?';
                          const totalItems = subValid?.total_items ?? '?';
                          const completionPct = subValid?.completion_rate != null
                            ? Math.round(subValid.completion_rate * 100)
                            : '?';
                          const missingPct = subValid?.missing_pct != null
                            ? Math.round(subValid.missing_pct * 100)
                            : null;

                          let validityTip = null;
                          if (isInvalid) {
                            validityTip = `${validItemCount} of ${totalItems} items answered (${completionPct}%). Needs at least 50% completion and 2+ valid items to be scorable.`;
                          } else if (action === 'prorate_flag') {
                            validityTip = `${missingPct}% of items were missing and have been estimated (prorated). ${validItemCount} of ${totalItems} items were answered. Treat this score with some caution.`;
                          } else if (action === 'caution') {
                            validityTip = `${missingPct}% of items were missing — this is a high rate. ${validItemCount} of ${totalItems} answered. This score should be treated with significant caution.`;
                          }

                          return (
                            <div key={sub} className={`flex items-center justify-between py-1.5 px-3 rounded-md text-sm ${
                              isInvalid ? 'bg-red-50' : hasProrateWarning ? 'bg-yellow-50' : 'hover:bg-gray-50'
                            }`}>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-800">{sub}</span>
                                {subLabel && <ClsBadge label={subLabel} />}
                                {isInvalid && (
                                  <Tooltip text={validityTip}>
                                    <span className="text-xs text-red-500 font-medium cursor-help underline decoration-dotted">
                                      (insufficient data: {validItemCount}/{totalItems} items)
                                    </span>
                                  </Tooltip>
                                )}
                                {hasProrateWarning && (
                                  <Tooltip text={validityTip}>
                                    <span className={`text-xs font-medium cursor-help underline decoration-dotted ${
                                      action === 'caution' ? 'text-orange-600' : 'text-yellow-600'
                                    }`}>
                                      {action === 'caution' ? '⚠ caution' : '⚠ estimated'}
                                      {` (${validItemCount}/${totalItems})`}
                                    </span>
                                  </Tooltip>
                                )}
                              </div>
                              <span className="text-sm text-gray-500 tabular-nums">
                                {subMean != null ? subMean.toFixed(2) : '—'}
                                {subMean != null && subValid?.valid && action === 'prorate' && (
                                  <InfoTip text={`Average of ${validItemCount} item scores in this subdomain. All items were answered.`} />
                                )}
                                {subMean != null && hasProrateWarning && (
                                  <InfoTip text={`Average includes prorated (estimated) values for ${totalItems - validItemCount} missing item(s). Based on ${validItemCount} of ${totalItems} items answered.`} />
                                )}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
      </div>
    );
  };

  // ── Phase 2 tab ──
  // Cross-context comparison, sensitivity detection, sensory profiling.
  const renderPhase2 = () => {
    const cpg = phase2.cpg || {};
    const agreement = phase2.agreement_index || {};
    const fatigue = phase2.fatigue || {};
    const predictability = phase2.predictability || {};
    const complexity = phase2.complexity || {};
    const sensory = phase2.sensory_profile || {};
    const spiky = phase2.spiky_profiles || {};
    const sai = phase2.sai || {};
    const itemDivFlags = phase2.item_divergence_flags || {};
    const subDivFlags = phase2.subdomain_divergence_flags || {};

    return (
      <div className="space-y-8">
        <ExplainBox>
          <strong>Phase 2 — Cross-Context Comparison</strong>: Now that Home and School are
          scored independently, we compare them. Do they agree? Where do they diverge?
          Are there cross-cutting patterns (like fatigue or sensory sensitivity) that explain
          why difficulties appear?
        </ExplainBox>

        {/* CPG & Agreement */}
        <section>
          <h4 className="text-lg font-bold text-gray-900 mb-1">Capacity-Performance Gap (CPG) & Agreement</h4>
          <p className="text-sm text-gray-600 mb-4">
            <strong>CPG</strong> measures how much the child's functioning differs between Home
            and School for each domain. A large gap (|CPG| ≥ 2.0) means they present very
            differently in the two settings. <strong>Agreement</strong> categorises the gap
            size: High (≤1), Moderate (1-2), or Low (&gt;2).
          </p>
          <div className="overflow-auto rounded-lg border border-gray-200">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-xs text-gray-600 uppercase">
                  <th className="px-4 py-2.5 text-left font-semibold">Domain</th>
                  <th className="px-4 py-2.5 text-left font-semibold">Home Mean</th>
                  <th className="px-4 py-2.5 text-left font-semibold">School Mean</th>
                  <th className="px-4 py-2.5 text-left font-semibold">
                    <span className="inline-flex items-center">
                      CPG
                      <InfoTip text="Context Performance Gap = School Mean minus Home Mean. Positive means better at school; negative means better at home." />
                    </span>
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold">
                    <span className="inline-flex items-center">
                      Agreement
                      <InfoTip text="How much Home and School agree. High (difference ≤ 1): similar views. Moderate (1–2): noticeable gap. Low (> 2): substantially different pictures." />
                    </span>
                  </th>
                  <th className="px-4 py-2.5 text-left font-semibold">
                    <span className="inline-flex items-center">
                      Divergent?
                      <InfoTip text="Flagged when |CPG| ≥ 2.0. This means the child's functioning looks meaningfully different across settings — worth investigating why." />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {Object.keys({ ...cpg, ...agreement }).sort().map((domain) => {
                  const c = cpg[domain] || {};
                  const a = agreement[domain] || {};
                  const cpgVal = c.cpg;
                  const isDivergent = c.divergent;
                  const agrColor = a.level === 'High' ? 'text-green-700' : a.level === 'Low' ? 'text-red-700' : 'text-yellow-700';
                  return (
                    <tr key={domain} className={isDivergent ? 'bg-red-50/40' : 'hover:bg-gray-50/50'}>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{domain}</td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700">
                        {c.home_mean != null ? c.home_mean.toFixed(2) : '—'}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums text-gray-700">
                        {c.school_mean != null ? c.school_mean.toFixed(2) : '—'}
                      </td>
                      <td className="px-4 py-2.5 tabular-nums font-medium">
                        {cpgVal != null ? cpgVal.toFixed(2) : '—'}
                      </td>
                      <td className={`px-4 py-2.5 font-medium ${agrColor}`}>
                        <Tooltip text={possibleValuesTooltip(AGREEMENT_OPTIONS, a.level)} maxWidth={360}>
                          <span className="cursor-help">{a.level || '—'}</span>
                        </Tooltip>
                      </td>
                      <td className="px-4 py-2.5">
                        {isDivergent ? (
                          <span className="text-red-600 font-semibold">⚠ Yes</span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        <hr className="border-gray-200" />

        {/* Divergence flags — only show when at least one non-null flag exists */}
        {(Object.values(subDivFlags).some((v) => v != null) || Object.values(itemDivFlags).some((v) => v != null)) && (
          <section>
            <h4 className="text-lg font-bold text-gray-900 mb-1">Divergence Flags</h4>
            <p className="text-sm text-gray-600 mb-4">
              Specific subdomains or items where Home and School scores differ significantly,
              highlighting areas where the child's experience varies most by context.
            </p>
            {Object.entries(subDivFlags).filter(([, v]) => v != null).length > 0 && (
              <div className="mb-3">
                <span className="text-sm font-semibold text-gray-700">Subdomain-level:</span>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {Object.entries(subDivFlags).filter(([, v]) => v != null).map(([key, info]) => {
                    const flagLabel = typeof info === 'string' ? info : (info?.diff?.toFixed(2) || '—');
                    return (
                      <Tooltip key={key} text={possibleValuesTooltip(DIVERGENCE_OPTIONS, typeof info === 'string' ? info : null)} maxWidth={380}>
                        <span className="px-2.5 py-1 text-xs rounded bg-amber-50 text-amber-800 border border-amber-200 cursor-help">
                          {key}: {flagLabel}
                        </span>
                      </Tooltip>
                    );
                  })}
                </div>
              </div>
            )}
            {Object.values(itemDivFlags).filter((v) => v != null).length > 0 && (
              <div>
                <span className="text-sm font-semibold text-gray-700">
                  Item-level: <span className="font-normal text-gray-600">{Object.values(itemDivFlags).filter((v) => v != null).length} item(s) flagged</span>
                </span>
              </div>
            )}
          </section>
        )}

        {(Object.values(subDivFlags).some((v) => v != null) || Object.values(itemDivFlags).some((v) => v != null)) && (
          <hr className="border-gray-200" />
        )}

        {/* Sensitivity flags */}
        <section>
          <h4 className="text-lg font-bold text-gray-900 mb-1">Sensitivity & Modulation Flags</h4>
          <p className="text-sm text-gray-600 mb-4">
            These detect whether specific cross-cutting factors amplify the child's difficulties.
            For example, a child might cope well in structured, predictable tasks but struggle
            when things are complex or unpredictable — that's a <em>predictability sensitivity</em>.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                label: 'Fatigue',
                data: fatigue,
                desc: 'Sleep, energy, or social interaction difficulties amplifying other areas',
                tip: 'Triggered when energy is rated "Much less" than peers, or when 2+ fatigue indicators (poor sleep, low energy, high need for rest after socialising) co-occur. Uses raw responses, not scores.',
              },
              {
                label: 'Predictability Sensitivity',
                data: predictability,
                desc: 'Difficulty increases when situations are unpredictable or routines change',
                tip: 'Triggered when flexibility/transition items (b1250, b1643, b1648) average ≤ -2, or a single item hits -3, or the transition item is ≤ -2 with a corroborating item. Uses internal scores.',
              },
              {
                label: 'Complexity Sensitivity',
                data: complexity,
                desc: 'Difficulty increases when tasks involve planning, sequencing, or multi-tasking',
                tip: 'Triggered when planning/sequencing item pairs are both ≤ -2, or a severe anchor hits -3, or the complexity items average ≤ -2 with the anchor present. Uses internal scores.',
              },
            ].map(({ label, data: flagData, desc, tip }) => (
              <div key={label} className="rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-gray-900">
                    {label}
                    <InfoTip text={tip} maxWidth={340} />
                  </span>
                  <div className="flex gap-1.5">
                    <FlagPill label="Home" active={flagData?.home?.active} />
                    <FlagPill label="School" active={flagData?.school?.active} />
                  </div>
                </div>
                <p className="text-sm text-gray-600">{desc}</p>
                {(flagData?.home?.active || flagData?.school?.active) && (
                  <div className="mt-2 text-sm text-gray-700 space-y-0.5">
                    {flagData?.home?.trigger && <div>Home trigger: <span className="font-medium">{flagData.home.trigger}</span></div>}
                    {flagData?.school?.trigger && <div>School trigger: <span className="font-medium">{flagData.school.trigger}</span></div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <hr className="border-gray-200" />

        {/* Sensory profile */}
        <section>
          <h4 className="text-lg font-bold text-gray-900 mb-1">Sensory Profile</h4>
          <p className="text-sm text-gray-600 mb-4">
            Per-sense tags and bucket classification (Priority Area / Support Need /
            No Difficulty) based on the pattern of responses for each sensory system.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {['home', 'school'].map((ctx) => {
              const profile = sensory[ctx] || {};
              const tags = profile.tags || {};
              const buckets = profile.buckets || {};
              const hasTags = Object.keys(tags).length > 0;
              return (
                <div key={ctx} className="rounded-lg border border-gray-200 p-4">
                  <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">{ctx}</div>
                  {!hasTags ? (
                    <span className="text-sm text-gray-500">No significant sensory tags</span>
                  ) : (
                    <div className="space-y-2">
                      {Object.entries(buckets).filter(([, b]) => b !== 'No Difficulty').map(([sense, bucket]) => (
                        <div key={sense} className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded-md ${
                            bucket === 'Priority Area' ? 'bg-red-100 text-red-800 border border-red-200' :
                            'bg-yellow-100 text-yellow-800 border border-yellow-200'
                          }`}>{bucket}</span>
                          <span className="text-sm font-medium text-gray-800">{sense.replace(/_/g, ' ')}</span>
                          <div className="flex flex-wrap gap-1">
                            {(tags[sense] || []).map((tag, i) => (
                              <Tooltip key={i} text={sensoryTagTooltip(tag)} maxWidth={300}>
                                <span className="px-1.5 py-0.5 text-[10px] rounded bg-indigo-50 text-indigo-700 border border-indigo-200 cursor-help">
                                  {sensoryTagToLabel(tag)}
                                </span>
                              </Tooltip>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <hr className="border-gray-200" />

        {/* Spiky profiles */}
        {spiky && (Object.keys(spiky).length > 0) && (
          <>
            <section>
              <h4 className="text-lg font-bold text-gray-900 mb-1">
                Spiky Profiles
                <InfoTip text="A 'spiky' profile means the child has both clear strengths and significant difficulties within the same domain. This intra-domain contrast is common in neurodivergent children and can be masked by domain-level averages." maxWidth={360} />
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Domains where the child shows both strengths and significant difficulties,
                revealing meaningful variability within a single area of functioning.
              </p>

              {(() => {
                const spikyDomains = Object.entries(spiky).filter(([, info]) => {
                  if (!info || typeof info !== 'object') return false;
                  return info.within_home || info.within_school || info.cross_context || info.cross_subdomain;
                });

                if (spikyDomains.length === 0) {
                  return <p className="text-sm text-gray-500 italic">No spiky profiles detected — subdomain scores are relatively consistent within each domain.</p>;
                }

                return (
                  <div className="space-y-3">
                    {spikyDomains.map(([domain, info]) => {
                      const patterns = [];
                      if (info.within_home) patterns.push({ label: 'Within Home', tip: 'Strength + impairment co-exist in home context' });
                      if (info.within_school) patterns.push({ label: 'Within School', tip: 'Strength + impairment co-exist in school context' });
                      if (info.cross_context) patterns.push({ label: 'Cross-Context', tip: 'Same subdomain is a strength in one setting but impaired in the other' });
                      if (info.cross_subdomain) patterns.push({ label: 'Cross-Subdomain', tip: 'Different subdomains: one is a strength in one setting, another is impaired in the other' });
                      const strengths = info.strength_subdomains || [];
                      const impaired = info.impaired_subdomains || [];

                      return (
                        <div key={domain} className="rounded-xl border border-gray-200 p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-sm font-bold text-gray-900">{domain}</span>
                            <div className="flex flex-wrap gap-1.5">
                              {patterns.map((p) => (
                                <Tooltip key={p.label} text={p.tip}>
                                  <span className="px-2 py-0.5 text-[10px] font-semibold rounded-md bg-amber-100 text-amber-800 border border-amber-200 cursor-help">
                                    {p.label}
                                  </span>
                                </Tooltip>
                              ))}
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {strengths.length > 0 && (
                              <div>
                                <div className="text-xs font-semibold text-green-700 mb-1">Strength subdomains</div>
                                <div className="flex flex-wrap gap-1">
                                  {strengths.map((s) => (
                                    <span key={s} className="px-2 py-0.5 text-xs rounded-md bg-green-50 text-green-800 border border-green-200">{s}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {impaired.length > 0 && (
                              <div>
                                <div className="text-xs font-semibold text-red-700 mb-1">Impaired subdomains</div>
                                <div className="flex flex-wrap gap-1">
                                  {impaired.map((s) => (
                                    <span key={s} className="px-2 py-0.5 text-xs rounded-md bg-red-50 text-red-800 border border-red-200">{s}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </section>
            <hr className="border-gray-200" />
          </>
        )}

        {/* Environmental SAI */}
        <section>
          <h4 className="text-lg font-bold text-gray-900 mb-1">Support Adequacy Index (SAI)</h4>
          <p className="text-sm text-gray-600 mb-4">
            Compares the number of impaired functional domains against the available
            environmental support. Are there enough environmental supports to match
            the child's needs? "Sufficient" means the environment is meeting needs;
            "Insufficient" means more support is needed.
          </p>
          <div className="grid grid-cols-2 gap-4">
            {['home', 'school'].map((ctx) => {
              const s = sai[ctx] || {};
              const status = s.status || '—';
              const statusCls = status === 'SUFFICIENT'
                ? 'bg-green-100 text-green-800'
                : status === 'INSUFFICIENT'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-600';
              return (
                <div key={ctx} className="rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold text-gray-700 uppercase tracking-wider">{ctx}</span>
                    <Tooltip text={possibleValuesTooltip(SAI_OPTIONS, status === '—' ? null : status)} maxWidth={380}>
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold cursor-help ${statusCls}`}>
                        {status}
                      </span>
                    </Tooltip>
                  </div>
                  <div className="mt-2 text-sm text-gray-600 space-y-0.5">
                    {s.impaired_domain_count != null && (
                      <div className="flex items-center">
                        {s.impaired_domain_count} impaired domain(s)
                        <InfoTip text="Number of functional domains with a mean of -2 or below (moderate/severe difficulty)." />
                      </div>
                    )}
                    {s.esi != null && (
                      <div className="flex items-center">
                        ESI: {s.esi}
                        <InfoTip text="Environmental Support Index: sum of all positive environmental item scores (+1 and +2 values). Higher = more environmental support available." />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    );
  };

  // ── Adjustments tab ──
  const renderAdjustments = () => {
    const hasHome = (reasonableAdjustments.home?.subdomains?.length || 0) +
                    (reasonableAdjustments.home?.supplementary?.length || 0) > 0;
    const hasSchool = (reasonableAdjustments.school?.subdomains?.length || 0) +
                      (reasonableAdjustments.school?.supplementary?.length || 0) > 0;

    // Collect debug notes from both contexts for the trace panel
    const homeDebug = reasonableAdjustments.home?.debug_notes || [];
    const schoolDebug = reasonableAdjustments.school?.debug_notes || [];
    const allDebug = [...homeDebug, ...schoolDebug];

    // Collapsible debug trace panel — shown regardless of whether RAs were
    // generated so we can always see the engine's reasoning.
    const renderDebugPanel = () => {
      if (!allDebug.length) return null;
      return (
        <details className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
          <summary className="px-4 py-2 bg-gray-50 text-xs font-semibold text-gray-600 cursor-pointer hover:bg-gray-100 select-none">
            RA Engine Debug Trace ({allDebug.length} notes)
          </summary>
          <div className="px-4 py-3 bg-white max-h-[40vh] overflow-y-auto">
            <div className="space-y-1">
              {allDebug.map((note, i) => (
                <p key={i} className="text-[11px] font-mono text-gray-600 leading-relaxed">
                  {note}
                </p>
              ))}
            </div>
          </div>
        </details>
      );
    };

    if (!hasHome && !hasSchool) {
      return (
        <div>
          <div className="text-gray-500 text-sm py-8 text-center">
            No reasonable adjustments generated. This typically means no subdomain
            reached the impairment threshold (mean at or below -1).
          </div>
          {renderDebugPanel()}
        </div>
      );
    }

    const strategyKey = raStrategy;
    const strategyDesc = strategyKey === 'option_a'
      ? 'RAs ranked by the total score of linked ICF codes (most impaired items first). Tag matches used as tiebreaker.'
      : 'RAs ranked by how many sensory/sensitivity tags match the child\'s profile, gated by at least one impaired ICF code. ICF score used as tiebreaker.';

    // Styling per bucket tier — mirrors the Canva design colour scheme
    const bucketStyles = {
      'Priority Area': {
        border: 'border-rose-300',
        bg: 'bg-rose-50',
        headerBg: 'bg-rose-100',
        headerText: 'text-rose-800',
        badge: 'bg-rose-200 text-rose-800',
      },
      'Support Need': {
        border: 'border-amber-300',
        bg: 'bg-amber-50',
        headerBg: 'bg-amber-100',
        headerText: 'text-amber-800',
        badge: 'bg-amber-200 text-amber-800',
      },
    };

    const renderTargetCard = (target, index) => {
      const adjustments = target[strategyKey] || [];
      if (!adjustments.length) return null;

      const isSupp = target.target_type === 'supplementary';
      const bucket = target.bucket;
      const style = bucketStyles[bucket] || bucketStyles['Support Need'];
      const meanLabel = target.subdomain_mean != null
        ? `subdomain: ${target.subdomain_mean}`
        : '';
      const domainMeanLabel = target.domain_mean != null
        ? `domain: ${target.domain_mean}`
        : '';
      const meansText = [meanLabel, domainMeanLabel].filter(Boolean).join(' | ');
      const triggerLabel = target.trigger ? ` — triggered by ${target.trigger}` : '';

      return (
        <div
          key={`${target.subdomain}-${target.domain}`}
          className={`rounded-lg border ${style.border} ${style.bg} p-4`}
        >
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs font-bold text-gray-400 w-5 text-right flex-shrink-0">
              {index + 1}.
            </span>
            <h4 className="font-semibold text-sm text-gray-800">
              {target.subdomain}
            </h4>
            <span className="text-xs text-gray-500">{target.domain}</span>
            {bucket && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${style.badge}`}>
                {bucket}
              </span>
            )}
            {isSupp && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium">
                Supplementary{triggerLabel}
              </span>
            )}
            {meansText && (
              <span className="text-[10px] text-gray-400 font-mono ml-auto">
                {meansText}
              </span>
            )}
          </div>
          <div className="space-y-2">
            {adjustments.map((adj, idx) => (
              <div key={idx} className="flex items-start gap-2 text-sm">
                <span className="text-gray-400 font-mono text-xs mt-0.5 w-4 text-right flex-shrink-0">
                  {idx + 1}.
                </span>
                <div className="flex-1">
                  <span className="text-gray-700">{adj.text}</span>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {adj.context_type && (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        adj.context_type === 'environmental' ? 'bg-blue-50 text-blue-700' :
                        adj.context_type === 'scaffolding' ? 'bg-purple-50 text-purple-700' :
                        'bg-green-50 text-green-700'
                      }`}>
                        {adj.context_type}
                      </span>
                    )}
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-mono">
                      ICF: {adj.icf_total_score}
                    </span>
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                      Tags: {adj.tag_match_count}
                    </span>
                    {adj.matched_sensory_tags?.map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-rose-50 text-rose-600">
                        {t}
                      </span>
                    ))}
                    {adj.matched_sensitivity_tags?.map((t) => (
                      <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-600">
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    };

    const renderContextColumn = (context, label) => {
      const ctxData = reasonableAdjustments[context] || {};
      const primary = ctxData.subdomains || [];
      const supplementary = ctxData.supplementary || [];
      const allTargets = [...primary, ...supplementary];
      if (!allTargets.length) {
        return (
          <div className="text-gray-400 text-sm py-4 text-center">
            No adjustments for {label}
          </div>
        );
      }

      // Split targets into bucket groups for visual separation
      const priorityTargets = allTargets.filter(t => t.bucket === 'Priority Area');
      const supportTargets = allTargets.filter(t => t.bucket === 'Support Need');
      const suppTargets = allTargets.filter(t => t.target_type === 'supplementary');

      // Global numbering across all targets within a context column
      let globalIndex = 0;

      return (
        <div className="space-y-5">
          {priorityTargets.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-3 w-3 rounded-full bg-rose-500 flex-shrink-0" />
                <h4 className="text-xs font-bold text-rose-800 uppercase tracking-wider">
                  Priority Areas
                </h4>
                <span className="text-[10px] text-rose-500">
                  ({priorityTargets.length} subdomain{priorityTargets.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="space-y-3">
                {priorityTargets.map((t) => {
                  const card = renderTargetCard(t, globalIndex);
                  globalIndex++;
                  return card;
                })}
              </div>
            </div>
          )}

          {supportTargets.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-3 w-3 rounded-full bg-amber-500 flex-shrink-0" />
                <h4 className="text-xs font-bold text-amber-800 uppercase tracking-wider">
                  Support Needs
                </h4>
                <span className="text-[10px] text-amber-500">
                  ({supportTargets.length} subdomain{supportTargets.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="space-y-3">
                {supportTargets.map((t) => {
                  const card = renderTargetCard(t, globalIndex);
                  globalIndex++;
                  return card;
                })}
              </div>
            </div>
          )}

          {suppTargets.length > 0 && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="h-3 w-3 rounded-full bg-violet-500 flex-shrink-0" />
                <h4 className="text-xs font-bold text-violet-800 uppercase tracking-wider">
                  Supplementary
                </h4>
                <span className="text-[10px] text-violet-500">
                  ({suppTargets.length} area{suppTargets.length !== 1 ? 's' : ''})
                </span>
              </div>
              <div className="space-y-3">
                {suppTargets.map((t) => {
                  const card = renderTargetCard(t, globalIndex);
                  globalIndex++;
                  return card;
                })}
              </div>
            </div>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-6">
        <ExplainBox>
          <strong>Reasonable Adjustments</strong> are personalised suggestions
          based on the child's impaired subdomains and active sensory/modulation
          tags. Subdomains are bucketed into <strong>Priority Areas</strong>{' '}
          (mean &le; -2) and <strong>Support Needs</strong> (-2 &lt; mean &le; -1).
          Two ranking strategies are available for comparison via the toggle below.
        </ExplainBox>

        {/* Bucket key */}
        <div className="flex items-center gap-4 text-xs text-gray-600">
          <span className="font-medium">Key:</span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-rose-500" />
            Priority Area (mean &le; -2)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
            Support Need (-2 &lt; mean &le; -1)
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-violet-500" />
            Supplementary (Sleep / Self-Care)
          </span>
        </div>

        {/* Strategy toggle */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-gray-700">Ranking strategy:</span>
          <div className="flex rounded-lg border border-gray-300 overflow-hidden">
            <button
              type="button"
              onClick={() => setRaStrategy('option_a')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                raStrategy === 'option_a'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Option A (ICF Score)
            </button>
            <button
              type="button"
              onClick={() => setRaStrategy('option_b')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                raStrategy === 'option_b'
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
            >
              Option B (Tag Match)
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 -mt-4">{strategyDesc}</p>

        {/* Home / School columns */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
              Adjustments at Home
            </h3>
            {renderContextColumn('home', 'Home')}
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
              Adjustments at School
            </h3>
            {renderContextColumn('school', 'School')}
          </div>
        </div>

        {/* Debug trace */}
        {renderDebugPanel()}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return renderOverview();
      case 'phase1': return renderPhase1();
      case 'phase2': return renderPhase2();
      case 'adjustments': return renderAdjustments();
      case 'raw': return <JsonPanel title="Complete Scoring Payload" data={data} defaultOpen />;
      default: return null;
    }
  };

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      <div className="flex gap-1.5 overflow-x-auto pb-1 border-b border-gray-200">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={`flex-shrink-0 px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-indigo-600 text-white'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* Content */}
      <div className="text-left">
        {renderContent()}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// MAIN DEMO PAGE
// ────────────────────────────────────────────────────────────────────────────

export default function HfpV2DemoPage() {
  // ── State ───────────────────────────────────────────────────────────────
  const [allAnswers, setAllAnswers] = useState(loadAnswers);
  const [respondent, setRespondent] = useState(loadRespondent);
  const [openFormKey, setOpenFormKey] = useState(null);
  const [simProfile, setSimProfile] = useState('typical');
  const [simAgreement, setSimAgreement] = useState('high');
  const [scoringResult, setScoringResult] = useState(() => loadSessionData(SCORING_KEY));
  const [scoringLoading, setScoringLoading] = useState(false);
  const [scoringError, setScoringError] = useState(null);
  const [reportResult, setReportResult] = useState(() => loadSessionData(REPORT_KEY));
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState(null);

  // Report personalisation — mirrors what production resolves from the
  // referral (child's first name + preferred pronoun set).
  const [childName, setChildName] = useState('Alex');
  const [pronouns, setPronouns] = useState('they/them');

  // LLM model selection. Reports are always LLM-enhanced (REPORT_MODE is
  // hardcoded to 'llm'). The model selector lets the reviewer choose which
  // LLM provider/model is used for paragraph polish and RA implementation.
  // The list is fetched from the backend so it always matches llm_router.
  const [llmModels, setLlmModels] = useState(FALLBACK_LLM_MODELS);
  const [selectedModel, setSelectedModel] = useState(FALLBACK_MODEL_ID);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../../firebase');
        const modelsFn = httpsCallable(functions, 'rmsHfpV2DemoModels');
        const res = await modelsFn({});
        if (cancelled) return;
        const models = res?.data?.models;
        if (Array.isArray(models) && models.length > 0) {
          setLlmModels(models);
          const fallback = res?.data?.default || models[0].id;
          setSelectedModel((prev) => (models.some((m) => m.id === prev) ? prev : fallback));
        }
      } catch (err) {
        console.warn('Could not load LLM model list, using fallback:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Full-screen mode hides everything except the report itself (no header,
  // forms, scoring panel, or generation controls). Useful for clean demos.
  const [fullScreen, setFullScreen] = useState(false);

  // Persist all session data to localStorage on every change
  useEffect(() => { saveAnswers(allAnswers); }, [allAnswers]);
  useEffect(() => {
    try { localStorage.setItem(RESPONDENT_KEY, respondent); } catch { /* noop */ }
  }, [respondent]);
  useEffect(() => { saveSessionData(SCORING_KEY, scoringResult); }, [scoringResult]);
  useEffect(() => { saveSessionData(REPORT_KEY, reportResult); }, [reportResult]);

  // ── Answer management ─────────────────────────────────────────────────
  const currentAnswers = useMemo(() => allAnswers[respondent] || {}, [allAnswers, respondent]);

  const setAnswer = useCallback((formKey, questionId, value) => {
    setAllAnswers((prev) => ({
      ...prev,
      [respondent]: {
        ...(prev[respondent] || {}),
        [formKey]: {
          ...((prev[respondent] || {})[formKey] || {}),
          [questionId]: value,
        },
      },
    }));
  }, [respondent]);

  const resetAll = useCallback(() => {
    setAllAnswers({ home: {}, school: {} });
    setScoringResult(null);
    setReportResult(null);
    clearAllSessionData();
  }, []);

  // ── Per-form progress ─────────────────────────────────────────────────
  // Conditional checkbox questions (Sensory seeking/avoidance modifiers)
  // only count toward the total when their gate question has the required
  // answer.  This prevents them from inflating the denominator and making
  // the progress bar appear incomplete when those questions are hidden.
  const formProgress = useMemo(() => {
    const progress = {};
    HFP_V2_SCHEMAS_LIST.forEach((schema) => {
      const formAnswers = currentAnswers[schema.formKey] || {};
      let total = 0;
      let answered = 0;
      (schema.sections || []).forEach((sec) => {
        (sec.questions || []).forEach((q) => {
          if (!isVisibleForRespondent(q, respondent)) return;
          const cond = q.meta?.hfp?.conditionalOn;
          if (cond) {
            const gateAnswer = formAnswers[cond.questionId];
            if (gateAnswer !== cond.value) return;
          }
          total++;
          if (formAnswers[q.questionId] != null) answered++;
        });
      });
      progress[schema.formKey] = total > 0 ? Math.round((answered / total) * 100) : 0;
    });
    return progress;
  }, [currentAnswers, respondent]);

  // ── Simulation ────────────────────────────────────────────────────────
  /**
   * Fills out every visible question for BOTH respondents using the
   * selected simulation profile. Uses a two-layer shift model to produce
   * realistic domain-level divergence between Home and School:
   *
   * Layer 1 (systematic): For each form/domain, decide ONE consistent
   *   shift direction and magnitude. This is the key fix — previously
   *   random per-item shifts would cancel out when averaged into domain
   *   means, making it nearly impossible to produce Low agreement or
   *   divergent CPGs at the domain level.
   *
   * Layer 2 (noise): Each item may receive additional small jitter on
   *   top of the systematic shift to avoid every item in a form being
   *   shifted by the exact same amount.
   */
  const simulateAll = useCallback(() => {
    const profile = SIMULATION_PROFILES[simProfile] || SIMULATION_PROFILES.typical;
    const agreement = CONTEXT_AGREEMENT_PRESETS[simAgreement] || CONTEXT_AGREEMENT_PRESETS.high;
    const generated = { home: {}, school: {} };

    HFP_V2_SCHEMAS_LIST.forEach((schema) => {
      generated.home[schema.formKey] = {};
      generated.school[schema.formKey] = {};

      // Layer 1: decide the systematic per-form shift once, applied to
      // all items in this form so domain means actually diverge.
      let formShift = 0;
      if (Math.random() < agreement.domainShiftProb) {
        const range = agreement.domainShiftMax - agreement.domainShiftMin;
        formShift = agreement.domainShiftMin + Math.round(Math.random() * range);
        const dir = Math.random() < agreement.directionBias ? 1 : -1;
        formShift *= dir;
      }

      (schema.sections || []).forEach((sec) => {
        (sec.questions || []).forEach((q) => {
          const options = q.options || [];
          if (options.length === 0) return;

          const hfpMeta = q.meta?.hfp || {};
          const homeVisible = isVisibleForRespondent(q, 'home');
          const schoolVisible = isVisibleForRespondent(q, 'school');

          // Handle checkbox (modifier) questions: conditionally simulate
          // based on whether the parent gate is answered "ao_1" (Much more).
          if (hfpMeta.stemType === 'Checkbox') {
            const cond = hfpMeta.conditionalOn;
            if (cond) {
              const gateHomeAnswer = generated.home[schema.formKey]?.[cond.questionId];
              const gateSchoolAnswer = generated.school[schema.formKey]?.[cond.questionId];
              // ~40% chance of ticking each modifier when gate is active
              if (homeVisible && gateHomeAnswer === cond.value) {
                generated.home[schema.formKey][q.questionId] = Math.random() < 0.4 ? 'ao_1' : 'ao_2';
              }
              if (schoolVisible && gateSchoolAnswer === cond.value) {
                generated.school[schema.formKey][q.questionId] = Math.random() < 0.4 ? 'ao_1' : 'ao_2';
              }
            }
            return;
          }

          // Standard radio questions (5-option or 6-option scales)
          const weights = options.length === 6 ? profile.weights6 : profile.weights5;

          const homeIdx = weightedRandom(weights);
          const safeHomeIdx = Math.min(homeIdx, options.length - 1);

          if (homeVisible) {
            generated.home[schema.formKey][q.questionId] = options[safeHomeIdx].value;
          }

          if (schoolVisible) {
            let totalShift = formShift;
            if (Math.random() < agreement.itemNoiseProb) {
              const noise = Math.ceil(Math.random() * agreement.itemNoise);
              totalShift += (Math.random() < 0.5 ? noise : -noise);
            }
            const schoolIdx = Math.max(0, Math.min(options.length - 1, safeHomeIdx + totalShift));
            generated.school[schema.formKey][q.questionId] = options[schoolIdx].value;
          }
        });
      });
    });

    setAllAnswers(generated);
    setScoringResult(null);
    setReportResult(null);
  }, [simProfile, simAgreement]);

  // ── Scoring ───────────────────────────────────────────────────────────
  /**
   * Calls the backend rmsHfpV2DemoScore cloud function. For now, we do
   * client-side scoring as a placeholder until the Python backend is built.
   * The actual scoring will be wired up when the cloud function is ready.
   */
  const runScoring = useCallback(async () => {
    setScoringLoading(true);
    setScoringError(null);
    try {
      // Placeholder: build a client-side summary of answers until the
      // Python scoring engine cloud function is deployed
      const homeAnswers = allAnswers.home || {};
      const schoolAnswers = allAnswers.school || {};

      // Call the cloud function when available
      // For now, show a structured summary of what would be sent
      const payload = { homeAnswers, schoolAnswers };

      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../../firebase');
        const scoreFn = httpsCallable(functions, 'rmsHfpV2DemoScore');
        const result = await scoreFn(payload);
        if (result.data?.error) {
          setScoringError(result.data.error);
          setScoringResult(result.data);
        } else {
          setScoringResult(result.data);
        }
      } catch (fnErr) {
        console.warn('Scoring cloud function error:', fnErr.message, fnErr.code, fnErr.details);
        setScoringError(`Cloud function error: ${fnErr.message}${fnErr.details ? ' — ' + JSON.stringify(fnErr.details) : ''}`);
      }
    } catch (err) {
      setScoringError(err.message || 'Scoring failed');
    } finally {
      setScoringLoading(false);
    }
  }, [allAnswers]);

  // ── Report generation ─────────────────────────────────────────────────
  // Sends scoring data, child name, pronouns, and the selected model id to
  // the cloud function, which delegates to the same build_full_report the
  // production HFP uses. The returned report carries the polished LLM
  // sections (_polished etc.) that HfpV2Report renders directly.
  const generateReport = useCallback(async () => {
    if (!scoringResult) return;
    setReportLoading(true);
    setReportError(null);
    try {
      try {
        const { httpsCallable } = await import('firebase/functions');
        const { functions } = await import('../../firebase');
        const reportFn = httpsCallable(functions, 'rmsHfpV2DemoReport', { timeout: 540000 });
        const result = await reportFn({
          scoring: scoringResult,
          childName: (childName || '').trim() || 'the child',
          pronouns,
          mode: REPORT_MODE,
          modelId: selectedModel,
        });
        if (result.data?.error) {
          setReportError(result.data.error);
          setReportResult(result.data);
        } else {
          setReportResult(result.data);
        }
      } catch (fnErr) {
        console.warn('Report cloud function error:', fnErr.message, fnErr.code, fnErr.details);
        setReportError(`Cloud function error: ${fnErr.message}${fnErr.details ? ' — ' + JSON.stringify(fnErr.details) : ''}`);
      }
    } catch (err) {
      setReportError(err.message || 'Report generation failed');
    } finally {
      setReportLoading(false);
    }
  }, [scoringResult, selectedModel, childName, pronouns]);

  // ── Render ────────────────────────────────────────────────────────────
  const openSchema = openFormKey ? HFP_V2_SCHEMAS[openFormKey] : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* ───── Header — hidden in full-screen demo mode ───── */}
      {!fullScreen && (
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-gray-200">
          <div className="mx-auto px-6 py-4" style={{ maxWidth: '80%' }}>
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl font-bold text-gray-900">HFP v2 Demo</h1>
                <p className="text-xs text-gray-500 mt-0.5">Internal review – Holistic Functional Profile v2</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={resetAll}
                  className="px-3 py-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                >
                  Reset All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* All controls (forms, simulation, scoring, report generation) are
         hidden in full-screen demo mode so only the report itself is visible. */}
      <div className={`mx-auto px-6 py-8 space-y-8${fullScreen ? ' hidden' : ''}`} style={{ maxWidth: '80%' }}>
        {/* ───── Respondent Toggle ───── */}
        <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
          <span className="text-sm font-medium text-gray-700">Respondent:</span>
          <div className="flex bg-gray-100 rounded-lg p-1">
            {Object.entries(RESPONDENT_LABELS).map(([key, label]) => (
              <button
                key={key}
                type="button"
                onClick={() => setRespondent(key)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                  respondent === key
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* ───── Form List ───── */}
        {openSchema ? (
          <DemoFormView
            schema={openSchema}
            answers={currentAnswers[openSchema.formKey] || {}}
            setAnswer={setAnswer}
            respondent={respondent}
            onClose={() => setOpenFormKey(null)}
          />
        ) : (
          <div className="space-y-3">
            <h2 className="text-lg font-semibold text-gray-900">Forms</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              {HFP_V2_SCHEMAS_LIST.map((schema) => {
                const progress = formProgress[schema.formKey] || 0;
                const formAnswers = currentAnswers[schema.formKey] || {};
                const totalQuestions = schema.sections.reduce(
                  (sum, sec) => sum + sec.questions.filter((q) => {
                    if (!isVisibleForRespondent(q, respondent)) return false;
                    const cond = q.meta?.hfp?.conditionalOn;
                    if (cond && formAnswers[cond.questionId] !== cond.value) return false;
                    return true;
                  }).length,
                  0
                );
                const sectionCount = schema.sections.filter((sec) =>
                  sec.questions.some((q) => isVisibleForRespondent(q, respondent))
                ).length;

                return (
                  <button
                    key={schema.formKey}
                    type="button"
                    onClick={() => setOpenFormKey(schema.formKey)}
                    className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-primary/40 hover:shadow-md transition-all group"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">
                        {schema.title}
                      </h3>
                      <StatusBadge progress={progress} />
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      {sectionCount} sections &middot; {totalQuestions} questions
                    </p>
                    {/* Progress bar */}
                    <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${
                          progress >= 100 ? 'bg-green-500' : progress > 0 ? 'bg-amber-400' : 'bg-gray-200'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ───── Simulate Panel ───── */}
        {!openSchema && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Simulate Answers</h2>
            <p className="text-sm text-gray-600 mb-4">
              Auto-fill all forms for both respondents with weighted-random answers.
              Choose a profile to test different scoring patterns.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={simProfile}
                onChange={(e) => setSimProfile(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {Object.entries(SIMULATION_PROFILES).map(([key, p]) => (
                  <option key={key} value={key}>{p.label}</option>
                ))}
              </select>
              <select
                value={simAgreement}
                onChange={(e) => setSimAgreement(e.target.value)}
                className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary"
              >
                {Object.entries(CONTEXT_AGREEMENT_PRESETS).map(([key, preset]) => (
                  <option key={key} value={key}>{preset.label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={simulateAll}
                className="px-5 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
              >
                Simulate All Answers
              </button>
            </div>
          </div>
        )}

        {/* ───── Scoring Panel ───── */}
        {!openSchema && (
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Scoring Engine</h2>
              <button
                type="button"
                onClick={runScoring}
                disabled={scoringLoading}
                className="px-5 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
              >
                {scoringLoading ? 'Running...' : 'Run Scoring Engine'}
              </button>
            </div>
            {scoringError && (
              <div className="text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{scoringError}</div>
            )}
            {scoringResult && (
              <ScoringDisplay data={scoringResult} />
            )}
          </div>
        )}

        {/* ───── Report Panel ───── */}
        {!openSchema && scoringResult && (
          <div className="space-y-4">
            {/* Report generation controls — mode toggle, model picker, and generate button */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">HFP v2 Report</h2>
                <button
                  type="button"
                  onClick={generateReport}
                  disabled={reportLoading || !scoringResult}
                  className="px-5 py-2 text-sm font-medium text-white bg-[#4a2c5a] rounded-lg hover:bg-[#3d2349] disabled:opacity-50 transition-colors shadow-sm"
                >
                  {reportLoading ? 'Generating...' : 'Generate Report'}
                </button>
              </div>

              {/* Personalisation + model controls — mirrors what production
                  resolves from the referral (name + pronouns). */}
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <label htmlFor="child-name" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Child name</label>
                  <input
                    id="child-name"
                    type="text"
                    value={childName}
                    onChange={(e) => setChildName(e.target.value)}
                    placeholder="the child"
                    className="w-32 px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4a2c5a]"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <label htmlFor="pronouns" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Pronouns</label>
                  <select
                    id="pronouns"
                    value={pronouns}
                    onChange={(e) => setPronouns(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4a2c5a]"
                  >
                    {PRONOUN_OPTIONS.map((p) => (
                      <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <label htmlFor="llm-model" className="text-xs font-medium text-gray-500 uppercase tracking-wide">Model</label>
                  <select
                    id="llm-model"
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#4a2c5a]"
                  >
                    {llmModels.map((m) => (
                      <option key={m.id} value={m.id}>{m.label}</option>
                    ))}
                  </select>
                </div>

                {/* Visual indicator of selected model */}
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {llmModels.find((m) => m.id === selectedModel)?.label || selectedModel}
                </span>
              </div>

              {/* Show metadata from last generation if available */}
              {reportResult?._llm_meta && (
                <div className="mt-3 text-xs text-gray-500 bg-gray-50 px-4 py-2 rounded-lg">
                  Last generation:{' '}
                  <strong>
                    {Object.values(reportResult._llm_meta.model_labels || {}).join(', ')
                      || reportResult._llm_meta.model_id
                      || (reportResult._llm_meta.mode === 'rules-only' ? 'Rules only' : 'LLM')}
                  </strong>
                  {reportResult._llm_meta.note && <span className="ml-2 text-gray-400">— {reportResult._llm_meta.note}</span>}
                </div>
              )}

              {reportError && (
                <div className="mt-3 text-sm text-red-600 bg-red-50 px-4 py-2 rounded-lg">{reportError}</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Full-width report display — breaks out of the 80% content container
          so the sidebar + content pane span the entire viewport width. The
          report is rendered by the shared HfpV2Report component (the same
          renderer used by the service-user and clinician HFP pages) so the
          demo always matches what service users see. */}
      {!openSchema && scoringResult && reportResult && !reportResult.error && (
        <div className={fullScreen ? 'fixed inset-0 z-50 bg-white flex flex-col' : 'w-full'}>
          <div className={`flex items-center justify-end gap-2 px-4 py-2 border-t border-b border-gray-200 bg-white${fullScreen ? ' flex-shrink-0' : ''}`}>
            <button
              type="button"
              onClick={() => setFullScreen((v) => !v)}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {fullScreen ? 'Exit full screen' : 'Full screen'}
            </button>
          </div>
          <div className={fullScreen ? 'flex-1 min-h-0' : 'shadow-sm'}>
            <HfpV2Report
              report={reportResult}
              childName={(childName || '').trim() || 'the child'}
              devMode={!fullScreen}
              pageMode={fullScreen}
            />
          </div>
          {/* Persistent footer disclaimer — full-width at the bottom of the report */}
          <div className={`bg-[#4a2c5a] text-white text-center text-xs font-medium tracking-wide py-2.5 px-4${fullScreen ? ' flex-shrink-0' : ''}`}>
            This is not a medical report and does not constitute medical advice
          </div>
        </div>
      )}
    </div>
  );
}
